"use client";

import { useState } from "react";

// Display-only multiplication table (2–9 × 2–9). Acts as a third "calculator
// mode" — a lookup aid with no computation. Hovering a body cell highlights its
// row/column headers and the cell so the factors are easy to read off.
const FACTORS = [2, 3, 4, 5, 6, 7, 8, 9];

export default function MultiplicationTable() {
  const [hover, setHover] = useState<{ row: number; col: number } | null>(null);

  return (
    <div className="w-full px-1 pb-2 select-text">
      <div
        role="table"
        aria-label="Multiplikationstabell, 2 till 9"
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${FACTORS.length + 1}, minmax(0, 1fr))` }}
        onMouseLeave={() => setHover(null)}
      >
        {/* Header row: corner cell + column factors */}
        <HeaderCell label="×" corner />
        {FACTORS.map((col) => (
          <HeaderCell
            key={`c-${col}`}
            label={String(col)}
            active={hover?.col === col}
          />
        ))}

        {/* Body rows: row factor header + products */}
        {FACTORS.map((row) => (
          <FragmentRow key={`r-${row}`}>
            <HeaderCell label={String(row)} active={hover?.row === row} />
            {FACTORS.map((col) => {
              const isCross = hover?.row === row && hover?.col === col;
              return (
                <div
                  key={`${row}x${col}`}
                  role="cell"
                  aria-label={`${row} gånger ${col} är ${row * col}`}
                  onMouseEnter={() => setHover({ row, col })}
                  className="min-h-9 flex items-center justify-center rounded text-sm tabular-nums transition-colors"
                  style={{
                    border: "1px solid var(--color-border)",
                    background: isCross
                      ? "var(--color-figur-mossa)"
                      : "var(--color-bg-card)",
                    color: isCross ? "white" : "var(--color-text)",
                    fontWeight: isCross ? 700 : 400,
                  }}
                >
                  {row * col}
                </div>
              );
            })}
          </FragmentRow>
        ))}
      </div>
    </div>
  );
}

function FragmentRow({ children }: { children: React.ReactNode }) {
  // The grid is flat (display:contents would break the role); render header +
  // cells as direct grid items via a fragment.
  return <>{children}</>;
}

function HeaderCell({
  label,
  active,
  corner,
}: {
  label: string;
  active?: boolean;
  corner?: boolean;
}) {
  return (
    <div
      role={corner ? "presentation" : "columnheader"}
      className="min-h-9 flex items-center justify-center rounded text-sm font-semibold tabular-nums transition-colors"
      style={{
        border: "1px solid var(--color-border)",
        background: active
          ? "color-mix(in oklch, var(--color-figur-mossa) 22%, var(--color-bg-card))"
          : "color-mix(in oklch, var(--color-figur-dimma) 35%, var(--color-bg-card))",
        color: "var(--color-text)",
      }}
    >
      {label}
    </div>
  );
}
